import { getApiBaseUrl } from '../config/api';

// FEAT-055 — Web API client for the per-visit Shift Report submit → review →
// approve/reject/escalate workflow (MVP-18.61, 18.62, 18.64). Mirrors the
// contract in docs/context/artifacts.yaml (FUNC-SubmitShiftReport,
// FUNC-ReviewShiftReport, FUNC-ApproveShiftReport, FUNC-RejectShiftReport,
// FUNC-EscalateShiftReport, FUNC-SetShiftReportBillingClassification).
//
// NEUTRAL-PLATFORM CONTRACT (MVP-18.64): this client only ever speaks of
// sending the report "to the client for review" and a CLIENT-initiated
// payment. There is no Careva-approves / Careva-pays / payroll concept here.

export type ShiftReportStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'ESCALATED';

export type BillingClassification = 'BILL_ONLY' | 'PAY_ONLY' | 'PAY_AND_BILL';

export interface ShiftReportChecklistItem {
  id: string;
  shiftReportId: string;
  category: string;
  item: string;
  status: string;
  note?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ShiftReport {
  id: string;
  appointmentId: string;
  caregiverId: string;
  careseekerId: string;
  status: ShiftReportStatus;
  visitStartAt?: string | null;
  visitEndAt?: string | null;
  totalHours?: string | null;
  mileage?: string | null;
  careNotes?: string | null;
  billingClassification?: BillingClassification | null;
  supervisorEscalationFlag: boolean;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  rejectionReason?: string | null;
  clientInitiatedPayment: boolean;
  paymentInitiatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  checklistItems: ShiftReportChecklistItem[];
}

export const BILLING_CLASSIFICATION_LABELS: Record<BillingClassification, string> = {
  BILL_ONLY: 'Bill Only',
  PAY_ONLY: 'Pay Only',
  PAY_AND_BILL: 'Pay and Bill',
};

class ShiftReportService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async parseError(response: Response, fallback: string): Promise<never> {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error?.message || fallback);
  }

  /** GET /shift-reports?bookingId= — list reports for a booking, newest first. */
  async listByBooking(bookingId: string): Promise<ShiftReport[]> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(
      `${baseUrl}/shift-reports?bookingId=${encodeURIComponent(bookingId)}`,
      { method: 'GET', headers: this.getAuthHeaders() }
    );
    if (!response.ok) {
      return this.parseError(response, 'Failed to load shift reports');
    }
    const result = await response.json();
    return Array.isArray(result.data) ? result.data : [];
  }

  /** GET /shift-reports/:id — fetch one report with its checklist items. */
  async getById(id: string): Promise<ShiftReport> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/shift-reports/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      return this.parseError(response, 'Failed to load shift report');
    }
    const result = await response.json();
    return result.data;
  }

  /**
   * POST /shift-reports/:id/review — the client opens a submitted report for
   * review (SUBMITTED → UNDER_REVIEW).
   */
  async markUnderReview(id: string): Promise<ShiftReport> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/shift-reports/${id}/review`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      return this.parseError(response, 'Failed to mark report under review');
    }
    const result = await response.json();
    return result.data;
  }

  /**
   * POST /shift-reports/:id/approve — the CLIENT approves the report
   * (UNDER_REVIEW → APPROVED). Payment is client-initiated; an optional billing
   * classification can be captured at approval time (MVP-18.62, 18.64).
   */
  async approve(
    id: string,
    billingClassification?: BillingClassification
  ): Promise<ShiftReport> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/shift-reports/${id}/approve`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(billingClassification ? { billingClassification } : {}),
    });
    if (!response.ok) {
      return this.parseError(response, 'Failed to approve shift report');
    }
    const result = await response.json();
    return result.data;
  }

  /** POST /shift-reports/:id/reject — the client rejects with a required reason. */
  async reject(id: string, reason: string): Promise<ShiftReport> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/shift-reports/${id}/reject`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ reason }),
    });
    if (!response.ok) {
      return this.parseError(response, 'Failed to reject shift report');
    }
    const result = await response.json();
    return result.data;
  }

  /**
   * POST /shift-reports/:id/escalate — Contact-Supervisor escalation
   * (MVP-18.61). Routes the flagged visit to admin review.
   */
  async escalate(id: string, reason: string): Promise<ShiftReport> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/shift-reports/${id}/escalate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ reason }),
    });
    if (!response.ok) {
      return this.parseError(response, 'Failed to escalate shift report');
    }
    const result = await response.json();
    return result.data;
  }

  /**
   * PUT /shift-reports/:id/billing-classification — capture the per-visit
   * billing classification for export (MVP-18.62).
   */
  async setBillingClassification(
    id: string,
    billingClassification: BillingClassification
  ): Promise<ShiftReport> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(
      `${baseUrl}/shift-reports/${id}/billing-classification`,
      {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ billingClassification }),
      }
    );
    if (!response.ok) {
      return this.parseError(response, 'Failed to set billing classification');
    }
    const result = await response.json();
    return result.data;
  }
}

export const shiftReportService = new ShiftReportService();
